import React, { useState } from 'react';
import { CheckCircle2, Loader2 } from 'lucide-react';
import { api } from '../context/AuthContext';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';

const EMPTY = {
  content_url: '', listing_id: '', content_type: 'photo', copyrighted_work: '',
  reporter_name: '', reporter_email: '', reporter_phone: '', reporter_address: '', signature: '',
};

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-sm text-brand-green/70 mb-1.5">{label}</span>
      {children}
    </label>
  );
}

export function DmcaReportForm() {
  const params = new URLSearchParams(window.location.search);
  const [f, setF] = useState({ ...EMPTY, listing_id: params.get('listing') || '', content_url: params.get('url') || '' });
  const [goodFaith, setGoodFaith] = useState(false);
  const [accurate, setAccurate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [doneId, setDoneId] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const body = { ...f, listing_id: f.listing_id || null, reporter_phone: f.reporter_phone || null,
        good_faith: goodFaith, accurate_under_penalty: accurate };
      const { data } = await api.post('/content-reports', body);
      setDoneId(data.id);
    } catch (err) {
      const d = err?.response?.data?.detail;
      setError(Array.isArray(d) ? 'Please complete every required field with valid values.' : (d || 'Could not submit the notice.'));
    } finally { setBusy(false); }
  };

  if (doneId) {
    return (
      <div data-testid="dmca-report-success" className="mt-6 p-6 rounded-2xl bg-white border border-brand-green/30 flex gap-3">
        <CheckCircle2 className="w-5 h-5 text-brand-green shrink-0 mt-0.5" />
        <div className="text-brand-green">
          <div className="font-semibold">Notice received</div>
          <p className="text-sm text-brand-green/70 mt-1">Reference <code data-testid="dmca-report-id">{doneId}</code>. Our team reviews every notice and will contact you at the email provided.</p>
        </div>
      </div>
    );
  }

  const cls = 'bg-brand-cream border-brand-cream-dark text-brand-green';
  return (
    <form onSubmit={submit} data-testid="dmca-report-form" className="mt-6 p-6 md:p-8 rounded-2xl bg-white border border-brand-cream-dark space-y-4">
      <Field label="URL of the infringing content *"><Input data-testid="dmca-content-url" required value={f.content_url} onChange={set('content_url')} className={cls} placeholder="https://… photo link or listing page" /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Listing ID (if known)"><Input data-testid="dmca-listing-id" value={f.listing_id} onChange={set('listing_id')} className={cls} /></Field>
        <Field label="Content type">
          <select data-testid="dmca-content-type" value={f.content_type} onChange={set('content_type')} className={`w-full h-10 rounded-md border px-3 text-sm ${cls}`}>
            <option value="photo">Photo</option><option value="video">Video</option>
            <option value="description">Listing description</option><option value="other">Other</option>
          </select>
        </Field>
      </div>
      <Field label="Describe your copyrighted work *"><Textarea data-testid="dmca-copyrighted-work" required value={f.copyrighted_work} onChange={set('copyrighted_work')} className={cls} placeholder="e.g. my original photo of 123 Main St, first published at …" /></Field>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Full name *"><Input data-testid="dmca-reporter-name" required value={f.reporter_name} onChange={set('reporter_name')} className={cls} /></Field>
        <Field label="Email *"><Input data-testid="dmca-reporter-email" type="email" required value={f.reporter_email} onChange={set('reporter_email')} className={cls} /></Field>
        <Field label="Phone"><Input data-testid="dmca-reporter-phone" value={f.reporter_phone} onChange={set('reporter_phone')} className={cls} /></Field>
        <Field label="Postal address *"><Input data-testid="dmca-reporter-address" required value={f.reporter_address} onChange={set('reporter_address')} className={cls} /></Field>
      </div>
      <label className="flex items-start gap-2 text-sm text-brand-green/80">
        <input type="checkbox" data-testid="dmca-good-faith" checked={goodFaith} onChange={(e) => setGoodFaith(e.target.checked)} className="mt-0.5 h-4 w-4 accent-brand-green" />
        I have a good-faith belief that this use is not authorized by the copyright owner, its agent, or the law.
      </label>
      <label className="flex items-start gap-2 text-sm text-brand-green/80">
        <input type="checkbox" data-testid="dmca-accurate" checked={accurate} onChange={(e) => setAccurate(e.target.checked)} className="mt-0.5 h-4 w-4 accent-brand-green" />
        Under penalty of perjury, the information in this notice is accurate and I am the owner or authorized to act on the owner&apos;s behalf.
      </label>
      <Field label="Electronic signature (type your full name) *"><Input data-testid="dmca-signature" required value={f.signature} onChange={set('signature')} className={cls} /></Field>
      {error && <p data-testid="dmca-report-error" className="text-sm text-red-600">{error}</p>}
      <button type="submit" data-testid="dmca-report-submit" disabled={busy || !goodFaith || !accurate}
        className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-brand-green text-brand-cream font-semibold hover:bg-brand-green-dark transition-colors disabled:opacity-60">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Submit DMCA notice
      </button>
    </form>
  );
}
